import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, forkJoin, map, Observable, switchMap, throwError } from 'rxjs';
import { PlanDetailsModel } from '../models/plan-details-model';
import { ClinetSubscriptionModel } from '../models/client-subscription-model';
import { environment } from '../../../../environments/environment';
import { SecureStorageService } from '../../../core/services/secure-storage.service';

/**
 * Every call on this service goes through the gateway: plans (8085-8087), client subscriptions
 * (8080-8084, 8088, 8100, 8116, 8126-8127), enterprise leads (8121, 8169, 8170, 8285), discounts
 * (8123-8125) and the AR usage counter (8283-8284).
 *
 * A plan's prices live in their own table (one row per billing_cycle). The plan*ToMicro /
 * planFromMicro pair below is the ONLY place that difference is visible: every component keeps
 * reading and writing the flat plan fields (plan_name, Contest_allowed, monthly_price,
 * status: 'Active'/'Inactive'/'Disable', ...).
 */
const PLAN_STATUS_TO_V2: Record<string, string> = { Active: 'active', Inactive: 'inactive', Disable: 'archived' };
const PLAN_STATUS_FROM_V2: Record<string, string> = { active: 'Active', draft: 'Inactive', inactive: 'Inactive', archived: 'Disable' };

/** the legacy flat plan row (billing_payment) -> the microservice's plan + prices[] input. */
function planToMicro(d: any): any {
  const toNum = (v: any) => (v === '' || v === null || v === undefined ? null : Number(v));
  const code = String(d.plan_name || '')
    .toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || `plan-${Date.now()}`;
  return {
    code,
    name: d.plan_name,
    description: d.description || null,
    status: PLAN_STATUS_TO_V2[d.status] || 'draft',
    is_enterprise_only: !!d.isEnterprise,
    contest_limit: toNum(d.Contest_allowed),
    participant_limit: toNum(d.Participants),
    integrated_contest_limit: toNum(d.Integrated_Contests),
    report_view: d.report_view || 'none',
    ads_view_access: !!d.Ads_View_Access,
    support_access: !!d.Support_Access,
    vip_view: !!d.VIP_View,
    web_access: !!d.web_access,
    ai_attributes_enabled: !!d.ai_attributes,
    ai_token_limit: toNum(d.max_tokens_allowed ?? d.ai_token_limit),
    ar_access: !!d.ar_access,
    ar_limit: toNum(d.ar_limit),
    ar_frequency: d.ar_frequency || null,
    sdk_enabled: !!d.sdk_features,
    sdk_price: toNum(d.sdk_price),
    sdk_billing_cycle: d.sdk_billing_cycle || null,
    ceiling_per_contest: toNum(d.ceiling_per_contest),
    min_expected_participants: toNum(d.min_expected_participants),
    // The percentage the budgets were worked out from. Stored on the plan so the form can show
    // it again instead of dividing the points back by the price to guess at it.
    points_budget_pct: toNum(d.points_budget_pct),
    // Not collected by the form — the schema's own documented default for a full completer.
    point_floor: 1,
    surplus_auto_roll_days: toNum(d.surplus_auto_roll_days),
    whatsapp_companion: !!d.whatsapp_companion,
    prices: [
      { billing_cycle: 'monthly', currency: d.currency || 'USD', amount: toNum(d.monthly_price) || 0, points_budget: toNum(d.points_month) || 0 },
      { billing_cycle: 'annually', currency: d.currency || 'USD', amount: toNum(d.yearly_price) || 0, points_budget: toNum(d.points_yearly) || 0 },
    ],
  };
}

/** The microservice's pivoted plan row -> the legacy flat billing_payment shape. */
function planFromMicro(p: any): any {
  return {
    id: p.id,
    plan_name: p.name,
    Contest_allowed: p.contest_limit,
    Participants: p.participant_limit,
    Integrated_Contests: p.integrated_contest_limit,
    description: p.description,
    monthly_price: p.monthly_price,
    yearly_price: p.yearly_price,
    currency: p.currency,
    points_month: p.points_month,
    points_yearly: p.points_yearly,
    ceiling_per_contest: p.ceiling_per_contest,
    min_expected_participants: p.min_expected_participants,
    surplus_auto_roll_days: p.surplus_auto_roll_days,
    status: PLAN_STATUS_FROM_V2[p.status] || 'Inactive',
    Ads_View_Access: p.ads_view_access,
    Support_Access: p.support_access,
    VIP_View: p.vip_view,
    web_access: p.web_access,
    sdk_features: p.sdk_enabled,
    ai_attributes: p.ai_attributes_enabled,
    report_view: p.report_view,
    isEnterprise: p.is_enterprise_only,
    max_tokens_allowed: p.ai_token_limit,
    ai_token_limit: p.ai_token_limit,
    ar_access: p.ar_access,
    ar_limit: p.ar_limit,
    ar_campaign_limit: p.ar_limit,
    // Unlimited is saved as a null limit but can come back as 0 — with AR on, both mean Unlimited.
    ar_campaign_unlimited: !!p.ar_access && (p.ar_limit == null || Number(p.ar_limit) === 0),
    ar_frequency: p.ar_frequency,
    ar_reset_frequency: p.ar_frequency,
    sdk_price: p.sdk_price,
    sdk_billing_cycle: p.sdk_billing_cycle,
    whatsapp_companion: p.whatsapp_companion,
    created_at: p.created_at,
  };
}

/** admin-subscription-list's flat row (subscriptions JOIN users/plans/plan_prices) -> the
 *  nested shape client-subscription.component.ts's grid reads (data.subscription.*). */
function clientSubFromMicro(r: any): any {
  return {
    id: r.client_id,
    subscription_id: r.id,
    name: r.client_name,
    company_name: r.company_name,
    client_company: { name: r.company_name },
    email: r.client_email,
    phone_number: r.phone_number,
    country: r.country_code,
    gender: r.gender,
    sdk_price: r.sdk_price,
    sdk_billing_cycle: r.sdk_billing_cycle,
    subscription: {
      id: r.id,
      plan_id: r.plan_id,
      plan: { type: r.plan_tier, name: r.plan_name, code: r.plan_code },
      type: r.provider,
      payment_status: r.status,
      start_date: r.period_start,
      end_date: r.period_end,
      created_at: r.created_at,
    },
  };
}

/** override-plan.component.ts's flat legacy-named payload -> subscription_entitlements'
 *  OVERRIDABLE column names (admin-override-plan-attributes, action 8088). Fields the v2
 *  entitlement snapshot doesn't model yet (sdk_price/sdk_billing_cycle/sdk_price_type/
 *  sdk_payment_status per-subscription, remaining_points, end_date, payment_status) are
 *  dropped here rather than silently sent nowhere — see the header note on this file.
 */
function overrideToMicro(d: any): any {
  const toNum = (v: any) => (v === '' || v === null || v === undefined ? null : Number(v));
  return {
    contest_limit: toNum(d.contest_allowed),
    participant_limit: toNum(d.Participants),
    integrated_contest_limit: toNum(d.Integrated_Contests),
    report_view: d.report_view || null,
    ads_view_access: d.Ads_View_Access,
    support_access: d.Support_Access,
    web_access: d.web_access,
    ai_attributes_enabled: d.ai_attributes,
    ai_token_limit: toNum(d.max_tokens_allowed ?? d.ai_token_limit),
    sdk_enabled: d.sdk_features,
    ar_access: d.ar_access,
    ar_limit: toNum(d.ar_limit),
    ar_frequency: d.ar_access ? (d.ar_frequency || null) : null,
    points_budget: toNum(d.points_budget),
    ceiling_per_contest: toNum(d.ceiling_per_contest),
    min_expected_participants: toNum(d.min_expected_participants),
    point_floor: toNum(d.point_floor),
    whatsapp_companion: d.whatsapp_companion,
  };
}

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  private readonly gatewayUrl = environment.loyaltyApiUrl;

  constructor(
    private http: HttpClient,
    private secureStorage: SecureStorageService
  ) {}

  private gatewayHeaders(skipLoader = false): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.secureStorage.getItem('authToken') || ''}`,
    });
    if (skipLoader) headers = headers.set('X-Skip-Loader', 'true');
    return headers;
  }

  private callAction<T = any>(actioncode: number, data: Record<string, any> = {}, skipLoader = false): Observable<T> {
    return this.http
      .post<any>(this.gatewayUrl, { actioncode, data }, { headers: this.gatewayHeaders(skipLoader) })
      .pipe(
        map((res) => {
          if (!res || res.isSuccess === false) {
            const message = res?.message || 'Request failed.';
            throw { message, error: { message, code: res?.code }, status: 400 };
          }
          return res.data as T;
        }),
        catchError((err) => throwError(() => err)),
      );
  }

  /** action 8085 · admin-plan-list. legacy returned a bare/paginated billing_payment list;
   *  search/status filtering is done here client-side since the catalogue is small. */
  getPlans(params?: any, skipLoader = false): Observable<any> {
    return this.callAction<{ plans: any[] }>(8085, { include_inactive: true, include_enterprise: true }, skipLoader).pipe(
      map((res) => {
        let items = (res.plans || []).map(planFromMicro);
        const search = String(params?.search || '').trim().toLowerCase();
        if (search) {
          items = items.filter((p) => String(p.plan_name || '').toLowerCase().includes(search));
        }
        if (params?.status) {
          items = items.filter((p) => p.status === params.status);
        }
        return { items, itemsTotal: items.length, pageTotal: 1 };
      }),
      catchError((error) => {
        return throwError(
          () => new Error(error?.error?.message || error?.message || 'Failed to load end users.')
        );
      })
    );
  }

  /** action 8121 · admin-enterprise-list, against public.enterprise_leads. Both `status`
   *  (active/inactive) and `request_status` (new/in_progress/closed) are now real, working
   *  filters — the backend derives both from the same underlying pipeline status, and the
   *  status TOGGLE (updateEnterpriseRequestStatus/acceptEnterprise) is ported too, action 8170. */
  getEnterprisePlans(params?: any, skipLoader = false): Observable<any> {
    const page = Number(params?.page) || 1;
    const perPage = Number(params?.per_page) || 20;
    return this.callAction<{ leads: any[]; total: number }>(8121, {
      page,
      per_page: perPage,
      search: params?.search || undefined,
      status: params?.status || undefined,
      request_status: params?.request_status || undefined,
      payment_method: params?.payment_method || undefined,
    }, skipLoader).pipe(
      map((res) => ({
        items: (res.leads || []).map((l: any) => ({
          id: l.id,
          company_name: l.company_name,
          name: l.contact_name,
          email: l.email,
          phone_number: l.phone,
          address: l.address,
          payment_method: l.preferred_payment_method,
          status: l.status === 'won' ? 'Active' : 'Inactive',
          request_status: ['won', 'lost', 'rejected'].includes(l.status)
            ? 'closed'
            : l.status === 'new' ? 'new' : 'in_progress',
          lead_status: l.status,
          // The client a 'won' lead was converted into, when the backend returns it.
          client_id: l.converted_client_id ?? l.client_id ?? null,
          created_at: l.created_at,
          u_date: l.updated_at,
        })),
        itemsTotal: res.total,
        curPage: page,
        pageTotal: Math.max(1, Math.ceil((res.total || 0) / perPage)),
      })),
      catchError((error) => {
        return throwError(
          () => new Error(error?.error?.message || error?.message || 'Failed to load enterprise requests.')
        );
      })
    );
  }

  /** Both status-dropdown columns funnel here, exactly as before — same signature, now backed
   *  by action 8170 instead of the legacy enterprise/accept. */
  updateEnterpriseRequestStatus(
    id: number | string,
    request_status: string,
    type: 'monthly' | 'annually' = 'monthly'
  ): Observable<any> {
    return this.acceptEnterprise(id, type, request_status);
  }

  /** action 8170 · admin-enterprise-set-status. Same {enterprise_id, type, request_status}
   *  contract as the old legacy enterprise/accept call — 'new'/'in_progress' just relabels the
   *  lead; 'closed' resolves the business (by its already-converted client, or by matching the
   *  lead's email against an existing user account) and converts it to a real subscription. */
  acceptEnterprise(
    enterpriseId: number | string,
    type: 'monthly' | 'annually',
    request_status: string = 'closed'
  ): Observable<any> {
    return this.callAction<any>(8170, {
      enterprise_id: enterpriseId,
      type,
      request_status,
    }).pipe(
      catchError((error) => {
        return throwError(
          () =>
            new Error(
              error?.message || 'Failed to accept enterprise request.'
            )
        );
      })
    );
  }

  /** Deactivate a converted enterprise: cancel its client's live subscription(s) immediately
   *  (action 8084). The lead has no subscription id, so the subscription is found through
   *  admin-subscription-list (8080) — by the converted client id when the lead carries one,
   *  otherwise by an exact match on the lead's email. */
  deactivateEnterprise(lead: any, reason?: string): Observable<number> {
    const clientId = lead?.client_id;
    const email = String(lead?.email || '').trim().toLowerCase();
    const cancellable = ['active', 'pending', 'past_due'];
    return this.callAction<{ subscriptions: any[] }>(8080, clientId
      ? { client_id: clientId, page: 1, perPage: 50 }
      : { search: email, page: 1, perPage: 50 }
    ).pipe(
      map((res) =>
        (res?.subscriptions || []).filter(
          (s: any) =>
            cancellable.includes(String(s.status || '').toLowerCase()) &&
            (clientId || String(s.client_email || '').trim().toLowerCase() === email)
        )
      ),
      switchMap((live) => {
        if (!live.length) {
          const message = 'No active subscription found for this enterprise.';
          return throwError(() => ({ message, error: { message } }));
        }
        return forkJoin(
          live.map((s: any) => this.callAction(8084, { subscription_id: String(s.id), immediate: true, reason: reason || null }))
        ).pipe(map(() => live.length));
      }),
      catchError((error) =>
        throwError(() => new Error(error?.error?.message || error?.message || 'Failed to deactivate enterprise.'))
      )
    );
  }

  /** action 8169 · admin-enterprise-get. */
  getEnterpriseById(id: number | string): Observable<any> {
    return this.callAction<any>(8169, { id }).pipe(
      catchError((error) => {
        return throwError(
          () =>
            new Error(
              error?.message || 'Failed to fetch enterprise request.'
            )
        );
      })
    );
  }

  /** action 8087 · admin-plan-update with archive:true. v2 has no hard-delete for a plan —
   *  archiving (plans.archived_at) is what listPlanCatalogue/listPlans already filter out,
   *  so the plan disappears from every catalogue the same way a legacy DELETE used to, without
   *  orphaning any subscription row that still points at this plan_id. */
  deleteSubscription(planId: number): Observable<void> {
    return this.callAction<void>(8087, { plan_id: planId, archive: true }).pipe(
      catchError((error) => {
        return throwError(
          () =>
            new Error(
              error?.error?.message || error?.message || 'Failed to delete subscription.'
            )
        );
      })
    );
  }

  /** action 8086 · admin-plan-create. subscriptionData is the form's flat legacy-shaped
   *  object (plan_name, Contest_allowed, monthly_price, ...) — planToMicro does the split
   *  into {plan, prices[]}. */
  addSubscription(subscriptionData: any): Observable<void> {
    return this.callAction(8086, planToMicro(subscriptionData)).pipe(
      catchError((error) => {
        return throwError(
          () =>
            new Error(error?.error?.message || error?.message || 'Failed to add subscription.')
        );
      })
    );
  }

  /** action 8087 · admin-plan-update. A price change (monthly_price/yearly_price/currency/
   *  points_month/points_yearly) is sent as `new_price` — the backend closes the old
   *  plan_prices row and opens a new one rather than editing the amount in place, so
   *  existing subscribers keep the price they originally bought. */
  updateSubscription(id: string | number, subscriptionData: any) {
    const micro = planToMicro(subscriptionData);
    const { prices, code, ...planFields } = micro;
    const cycle = subscriptionData.mode === 'annually' || subscriptionData.mode === 'yearly' ? 'annually' : 'monthly';
    const newPrice = prices.find((p: any) => p.billing_cycle === cycle);
    return this.callAction(8087, { plan_id: id, ...planFields, new_price: newPrice }).pipe(
      catchError((error) => {
        return throwError(
          () =>
            new Error(
              error?.error?.message || error?.message || 'Failed to update subscription.'
            )
        );
      })
    );
  }

  /** action 8080 · admin-subscription-list. the legacy client_subsucription accepted many more
   *  filters (gender, plan_type, country, sector, payment_type, cash_approve, ...) than the
   *  backend currently supports — only search/status/paging actually narrow the result here;
   *  the rest are accepted from the UI and silently ignored rather than erroring. */
  getClientSubsucriptions(params?: any, skipLoader = false): Observable<any> {
    const perPage = Number(params?.per_page) || 20;
    return this.callAction<{ subscriptions: any[]; total: number }>(8080, {
      page: params?.page,
      perPage,
      search: params?.search || undefined,
      status: params?.subscription_status || undefined,
    }, skipLoader).pipe(
      map((res) => ({
        items: (res.subscriptions || []).map(clientSubFromMicro),
        itemsTotal: res.total ?? (res.subscriptions || []).length,
        pageTotal: Math.max(1, Math.ceil((res.total ?? 0) / perPage)),
      })),
      catchError((error) => {
        return throwError(
          () =>
            new Error(
              error?.error?.message || error?.message || 'Failed to load clients subscriptions.'
            )
        );
      })
    );
  }

  /** action 8080 · admin-subscription-list with sdk_only:true (subscription_entitlements.
   *  sdk_enabled) — the legacy sdk/client_subsucription filtered the same way. */
  getSdkClientSubscriptions(params?: any, skipLoader = false): Observable<any> {
    const perPage = Number(params?.per_page) || 20;
    return this.callAction<{ subscriptions: any[]; total: number }>(8080, {
      sdk_only: true, page: params?.page, perPage, search: params?.search || undefined,
    }, skipLoader).pipe(
      map((res) => ({
        items: (res.subscriptions || []).map(clientSubFromMicro),
        itemsTotal: res.total ?? (res.subscriptions || []).length,
        pageTotal: Math.max(1, Math.ceil((res.total ?? 0) / perPage)),
      })),
      catchError((error) => {
        return throwError(
          () =>
            new Error(
              error?.error?.message || error?.message || 'Failed to load SDK client subscriptions.'
            )
        );
      })
    );
  }

  /** action 8080 · admin-subscription-list, filtered to one client — every subscription row
   *  that client has ever had (the legacy plan_History). */
  getClientSubscriptionHistoryPaginated(userId: string, params?: any, skipLoader = false): Observable<{
    items: any[];
    totalItems: number;
    currentPage: number;
    totalPages: number;
  }> {
    const perPage = Number(params?.per_page) || 20;
    return this.callAction<{ subscriptions: any[]; total: number }>(8080, {
      client_id: userId, page: params?.page, perPage, search: params?.search || undefined,
    }, skipLoader).pipe(
      map((response: any) => {
        const items = (response?.subscriptions || []).map((r: any) => ({
          id: r.id,
          _subscription_plan: { type: r.plan_tier, mode: r.billing_cycle, name: r.plan_name },
          _client_company: { name: r.company_name },
          type: r.provider === 'stripe' ? 'online' : r.provider,
          paid_amount: r.amount,
          start_date: r.period_start,
          end_date: r.period_end,
          payment_status: r.status,
          created_at: r.created_at,
        }));
        const totalItems = response?.total ?? items.length;
        const currentPage = params?.page ?? 1;
        const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
        return { items, totalItems, currentPage, totalPages };
      }),
      catchError((error) => {
        return throwError(
          () => new Error(error?.error?.message || error?.message || 'Failed to load client subscription history.')
          );
        })
      );
  }

  /** action 8081 · admin-subscription-get — one subscription with its entitlement snapshot
   *  (subscription_plan), owning client and event trail. */
  getSubscriptionDetail(subscriptionId: string, skipLoader = false): Observable<any> {
    return this.callAction(8081, { subscription_id: subscriptionId }, skipLoader).pipe(
      catchError((error) => throwError(
        () => new Error(error?.error?.message || error?.message || 'Failed to load subscription details.')
      ))
    );
  }

  /** action 8083 · admin-subscription-update with status 'active' — activates a pending
   *  subscription (stamps activated_at + writes the event). */
  activateSubscription(subscriptionId: string, reason?: string): Observable<any> {
    return this.callAction(8083, { subscription_id: subscriptionId, status: 'active', reason: reason || null }).pipe(
      catchError((error) => throwError(
        () => new Error(error?.error?.message || error?.message || 'Failed to activate subscription.')
      ))
    );
  }

  /** action 8084 · admin-subscription-cancel. immediate:false keeps access until period end
   *  (same as a client cancel); immediate:true ends it now. */
  cancelClientSubscription(subscriptionId: string, immediate: boolean, reason?: string): Observable<any> {
    return this.callAction(8084, { subscription_id: subscriptionId, immediate, reason: reason || null }).pipe(
      catchError((error) => throwError(
        () => new Error(error?.error?.message || error?.message || 'Failed to cancel subscription.')
      ))
    );
  }

  /** action 8082 · admin-subscription-create, provider forced to 'cash'. createSubscription
   *  applies priceOffer() same as everywhere else, so an active discount on this plan is
   *  picked up automatically — no separate discount handling needed here.
   *
   *  activate:false stages a PENDING cash request; the client keeps their current plan until it
   *  is approved. "Paid" then approves it through 8100 (expires the old plan, writes the paid
   *  invoice, mails the client); "Unpaid" leaves it in the cash queue. Without this flag 8082
   *  activated the plan itself and 8100 answered ALREADY_DECIDED. */
  cashPaymentByAdmin(data: any): Observable<any> {
    return this.callAction(8082, {
      client_id: data.user_id ?? data.client_id,
      plan_id: data.plan_id,
      type: data.type,
      provider: 'cash',
      activate: false,
    }).pipe(
      catchError((error) => {
        return throwError(
          () =>
            new Error(
              error?.error?.message || error?.message || 'Failed to update the clients subscriptions.'
            )
        );
      })
    );
  }

  /** action 8127 · admin-get-client-subscription — feeds the Override screen's load. */
  getSubscriptionById(userId: string | number): Observable<any> {
    return this.callAction(8127, { client_id: userId }).pipe(
      catchError((error) => {
        return throwError(
          () =>
            new Error(
              error?.error?.message || error?.message || 'Failed to fetch subscription details.'
            )
        );
      })
    );
  }

  /** action 8088 · admin-override-plan-attributes. user_id is the client id — the backend
   *  resolves their live (active/pending/past_due) subscription and overrides its entitlement
   *  snapshot, never the plan catalogue. */
  addOverridePlan(subscriptionData: any): Observable<void> {
    return this.callAction<void>(8088, {
      client_id: subscriptionData.user_id,
      // Passed straight through rather than mapped: it is not an entitlement column, it is the
      // note written beside the override. overrideEntitlements refuses without it.
      reason: subscriptionData.reason,
      ...overrideToMicro(subscriptionData),
    }).pipe(
      catchError((error) => {
        return throwError(
          () =>
            new Error(error?.error?.message || error?.message || 'Failed to add subscription.')
        );
      })
    );
  }

  /** action 8116 · admin-override-sdk-feature. The per-business SDK arrangement — price,
   *  cycle, how it is paid and whether it has been — written to client_sdk_grants.
   *
   *  This posted to legacy until now, so the four SDK controls on the Override screen were saved
   *  to a system that holds none of this and came back empty on every visit.
   *
   *  success_url / cancel_url are still accepted from the caller and deliberately NOT sent: the
   *  Stripe checkout half is not ported, and the action says so in its reply rather than
   *  leaving the admin to assume a payment link went out. */
  overrideSdkFeature(data: {
    // A uuid, like every other id here — this said `number`, which is how they became NaN.
    user_id: string;
    sdk_features: boolean;
    sdk_price?: string | number;
    sdk_billing_cycle?: string;
    success_url?: string;
    cancel_url?: string;
    payment_method?: string;
    payment_status?: string;
    status?: string;
    is_active?: boolean;
    message?: string;
  }): Observable<any> {
    return this.callAction<any>(8116, {
      client_id: String(data.user_id ?? ''),
      sdk_features: data.sdk_features,
      sdk_price: data.sdk_price,
      sdk_billing_cycle: data.sdk_billing_cycle,
      payment_method: data.payment_method,
      payment_status: data.payment_status,
      message: data.message,
    }).pipe(
      catchError((error) => throwError(
        () => new Error(error?.error?.message || error?.message || 'Failed to override SDK feature.')
      ))
    );
  }

  /**
   * Approve/reject cash payment — used after override_sdk_feature when admin
   * selects "paid" to ensure the backend request module flips to paid status
   * and sends the correct email.
   */
  /** action 8100 · admin-cash-approve — approve or reject the newest pending cash request for this client. */
  approveCashPayment(data: {
    user_id: number | string;
    isApprove: boolean;
    message?: string;
  }): Observable<any> {
    return this
      .callAction<any>(8100, { client_id: data.user_id, isApprove: data.isApprove, message: data.message })
      .pipe(
        catchError((error) => {
          return throwError(
            () =>
              new Error(error.error?.message || 'Failed to update cash approval.')
          );
        })
      );
  }

  getSubscriptionPlansList(params?: any, skipLoader = false): Observable<any> {
    return this.getPlans(params, skipLoader);
  }

  /** action 8126 · admin-get-client-plan-info. */
  getPlanInfo(userId: string | number): Observable<any> {
    return this.callAction(8126, { client_id: userId }).pipe(
      catchError((error) => {
        return throwError(
          () =>
            new Error(error?.error?.message || error?.message || 'Failed to fetch plan info.')
        );
      })
    );
  }

  /** action 8127 · admin-client-subscription. Reshaped so `res.subscription_plan.Participants`
   *  (what add-contests.component.ts reads) matches the legacy flatter get_active_subscription_plan
   *  shape — the backend nests it one level deeper, under `subscription`. */
  getActiveSubscriptionPlan(userId: string | number): Observable<any> {
    return this.callAction<any>(8127, { client_id: userId })
      .pipe(
        map((res: any) => res?.subscription ?? res),
        catchError((error) => {
          return throwError(
            () =>
              new Error(error?.message || 'Failed to fetch active subscription plan.')
          );
        })
      );
  }

  /** action 8123 · admin-list-discounts, against public.plan_price_offers. */
  getRenewalDiscounts(params?: any, skipLoader = false): Observable<any> {
    return this.callAction(8123, {
      page: params?.page, per_page: params?.per_page, search: params?.search,
      status: params?.status, plan_type: params?.plan_type,
    }, skipLoader).pipe(
      catchError((error) => {
        return throwError(
          () =>
            new Error(
              error?.error?.message || error?.message || 'Failed to load discounted plans.'
            )
        );
      })
    );
  }

  /** action 8124 · admin-create-discount. Same input shape the legacy renewal_discount POST
   *  took — user_id, billing_payment_id (now a v2 plan id), discount_type, both cycle
   *  values, valid_days, monthly/yearly_operation. Stacks onto any existing active
   *  discount on the same plan exactly as legacy did. */
  addRenewalDiscount(discountData: any): Observable<any> {
    return this.callAction(8124, discountData).pipe(
      catchError((error) => {
        return throwError(
          () =>
            new Error(
              error?.error?.message || error?.message || 'Failed to create renewal discount.'
            )
        );
      })
    );
  }

  /** action 8125 · admin-cancel-discount. */
  cancelRenewalDiscount(discountId: number, userId: number): Observable<any> {
    return this.callAction(8125, { discount_id: discountId }).pipe(
      catchError((error) => {
        return throwError(
          () =>
            new Error(
              error?.error?.message || error?.message || 'Failed to cancel discount.'
            )
        );
      })
    );
  }

  /** action 8085 · admin-plan-list, narrowed to one plan, in the flat shape the plan form reads. */
  getPlanById(id: string | number): Observable<any> {
    return this.callAction<{ plans: any[] }>(8085, { include_inactive: true, include_enterprise: true }).pipe(
      map((res) => {
        const plan = (res.plans || []).find((p: any) => String(p.id) === String(id));
        if (!plan) throw new Error('Subscription plan not found.');
        return planFromMicro(plan);
      }),
      catchError((error) => {
        return throwError(
          () => new Error(error?.error?.message || error?.message || 'Failed to fetch subscription plan.')
        );
      })
    );
  }

  /** action 8285 · admin-enterprise-delete. */
  deleteEnterprise(enterpriseId: number | string): Observable<void> {
    return this.callAction<void>(8285, { id: enterpriseId }).pipe(
      catchError((error) => {
        return throwError(
          () => new Error(error?.error?.message || error?.message || 'Failed to delete enterprise.')
        );
      })
    );
  }

  /**
   * action 8081 · admin-subscription-get, reshaped into the invoice the PDF builder reads
   * (client / subscription / pricing / payment blocks).
   */
  getInvoiceDetails(subscriptionId: number | string): Observable<any> {
    return this.callAction<any>(8081, { subscription_id: subscriptionId }, true).pipe(
      map((res: any) => {
        const sub = res?.subscription || {};
        const client = res?.client || {};
        const amount = Number(sub.amount ?? 0);
        const listAmount = Number(sub.list_amount ?? sub.amount ?? 0);
        const discounted = listAmount > amount;
        // A plan that was activated was paid for, even if a later plan change has since expired it.
        const status = String(sub.status || '').toLowerCase();
        const paid = status === 'active' || (!!sub.activated_at && status !== 'past_due');
        return {
          invoice_number: `INV-${String(sub.id ?? subscriptionId).slice(0, 8).toUpperCase()}`,
          invoice_date: sub.activated_at || sub.created_at || null,
          client: {
            name: client.name,
            company_name: client.company_name,
            email: client.email,
            phone: client.phone_number,
            country: null,
            company_address: null,
            trn_vat_number: null,
          },
          subscription: {
            id: sub.id,
            description: [sub.plan_name, sub.billing_cycle].filter(Boolean).join(' - '),
            billing_cycle: sub.billing_cycle,
            status: sub.status,
            payment_status: paid ? 'paid' : 'unpaid',
            period: { start: sub.period_start, end: sub.period_end },
          },
          plan_attributes: { billing_cycle: sub.billing_cycle },
          pricing: {
            currency_code: sub.currency || 'USD',
            unit_price: listAmount,
            quantity: 1,
            discount: discounted ? { label: 'Plan discount', amount: listAmount - amount } : null,
            sdk_addon: null,
            subtotal: amount,
            vat: null,
            total: amount,
            paid_amount: paid ? amount : 0,
          },
          payment: {
            method: sub.provider,
            reference: sub.provider_subscription_id || null,
            date: sub.activated_at || null,
            stripe_customer_id: sub.provider_customer_id || null,
          },
        };
      }),
      catchError((error) => {
        return throwError(
          () => new Error(error?.error?.message || error?.message || 'Failed to fetch invoice details.')
        );
      })
    );
  }

  /**
   * action 8283 · admin-client-ar-usage. AR / Treasure Hunt usage for a client:
   * ar_access / ar_limit / ar_used / ar_remaining / ar_reset_date / ar_frequency / campaigns.
   */
  getArUsage(userId: number | string): Observable<any> {
    return this.callAction<any>(8283, { client_id: userId }).pipe(
      catchError((error) => {
        return throwError(
          () => new Error(error?.error?.message || error?.message || 'Failed to fetch AR usage.')
        );
      })
    );
  }

  /** action 8284 · admin-client-ar-usage-reset. Starts a new AR counting period for the client. */
  resetArUsage(userId: number | string): Observable<any> {
    return this.callAction<any>(8284, { client_id: userId }).pipe(
      catchError((error) => {
        return throwError(
          () => new Error(error?.error?.message || error?.message || 'Failed to reset AR usage counter.')
        );
      })
    );
  }
}
